/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  List,
  makeStyles
} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {getCVSAssets} from '@ion/core/src/services/ArcGIS';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import Device from '@ion/core/src/services/Device';
import {TagReportType, ToolAssetType} from '@ion/core/src/types/entities';
import {TAG_ASSET_TYPE_ICONS, TagAssetType} from '@ion/dls/src/components/assetdetails/current/TagTitleRow';
import {ToolCard, ToolCardProps} from '@ion/dls/src/components/assetdetails/current/ToolCard';
import Divider from '@ion/dls/src/components/divider/Divider';
import AssetDetailListItem from '@ion/dls/src/components/sidepanel/AssetDetailListItem';
import useMedia from '@ion/dls/src/hooks/useMedia';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {getFormattedDateTimeZoneParts} from '@ion/dls/src/utils/ComponentUtils';
import {Route} from '@/routes/RouteDefinitions';
import {onAssetSelected, setAssetDetailView} from '@/store/actions/tracking';
import {
  disconnectedToolTagReportSelector,
  searchSelectedSelector,
  selectedAssetSelector,
  selectedToolSelector,
  toolDetailViewSelector
} from '@/store/selectors/tracking-selectors';
import {useSelectAssetAndResets} from './hooks/sidesheet/useSelectAssetAndResets';
import AssetCard from './AssetCard';
import IonTagDetailsPanel from './IonTagDetailsPanel';
import ToolCardListContent from './ToolCardListContent';

const useStyles = makeStyles(theme => ({
  toolDetailWrapper : {

  }
}));

// a tag report's address as one line, or `undefined` without one ...
const formatToolAddress = (tagReport?: TagReportType): string | undefined => {
  const address = tagReport?.address;
  const parts   = [
    address?.address1 || address?.street,
    address?.city,
    address?.stateProvinceCd || address?.state
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : undefined;
};

export interface ToolCardPropsOptions {
  tool: ToolAssetType;
  parentAsset?: Device;
  disconnectedTagReport?: TagReportType;
  onOpenParent: () => void;
  onOpenAsset: (assetId: string) => void;
}

/**
 * Maps a tool - and the asset whose details it was opened from - to the
 * current theme's `ToolCard`: in range while it reports, its location from
 * its own or its last tag report (and that report's time while it is out of
 * range), and whom it is assigned to - that asset, another one, or none.
 * Anything with no data reads as unknown.
 *
 * ```typescript
 * import {getToolCardProps} from './TagTrackedAssetsPanel';
 *
 * ...
 *
 * const cardProps = getToolCardProps({tool, parentAsset, onOpenParent, onOpenAsset});
 * ```
 *
 * @param options The tool, its parent asset, its last tag report and the
 *                assignment link's actions.
 * @returns       The props `ToolCard` renders.
 */
export const getToolCardProps = ({
  tool,
  parentAsset,
  disconnectedTagReport,
  onOpenParent,
  onOpenAsset
}: ToolCardPropsOptions): ToolCardProps => {
  const unknown    = translate('tracking:toolCard.unknown');
  const isInRange  = !!tool.tagReport;
  const reportTime = !isInRange && disconnectedTagReport?.eventTime;
  const timeParts  = reportTime ? getFormattedDateTimeZoneParts(new Date(reportTime)) : null;
  const assignedId = tool.asset ? `${entityId(tool.asset) ?? ''}` : '';
  const parentId   = `${parentAsset?.getAssetValue?.('assetId') ?? parentAsset?.id?.() ?? ''}`;
  const isParent   = !!assignedId && assignedId === parentId;
  const typeKey    = tool.assetType?.toLowerCase() as TagAssetType;
  let status: ToolCardProps['status'] = 'unassigned';
  if (assignedId) {
    status = isParent ? 'assigned' : 'assignedToOther';
  }
  return {
    assetType         : typeKey in TAG_ASSET_TYPE_ICONS ? typeKey : 'tool',
    assetName         : tool.name || unknown,
    assetTypeLabel    : tool.assetType || unknown,
    range             : isInRange ? 'in' : 'out',
    status,
    location          : formatToolAddress(tool.tagReport) ?? formatToolAddress(disconnectedTagReport) ?? unknown,
    assignedAssetName : (isParent ? parentAsset?.name?.() : tool.asset?.title) || unknown,
    lastReportLabel   : translate('tracking:toolCard.lastReport', {
      time : timeParts ? `${timeParts.time} ${timeParts.date}` : unknown
    }),
    unassignedLabel      : translate('tracking:toolCard.unassigned'),
    rangeLabel           : translate(`tracking:toolCard.${isInRange ? 'inRange' : 'outOfRange'}`),
    onAssignedAssetClick : isParent ? onOpenParent : function() {
      onOpenAsset(assignedId);
    }
  };
};

/**
 * The entire panel that populates the sidepanel when a tool is in view
 *
 * ```typescript
 * import React from 'react';
 * import TagTrackedAssetsPanel from '@/routes/tracking/PanelList';
 *
 * <TagTrackedAssetsPanel assetData={assetData}
 *   toolDetailView={true}
 *   toolPanelLabels={toolPanelLabels}
 *   toolConnected={false}
 *   disconnectedToolTagReport={disconnectedToolTagReport} />
 *
 * ```
 */
const TagTrackedAssetsPanel = () => {
  const classes                         = useStyles();
  const [toolInfoOpen, setToolInfoOpen] = useState(false);
  const toolPanelLabels                 = useMemo(() => ({
    ...translate('tracking:toolDetail', {returnObjects: true})
  }), []);
  const parentDevice               = useSelector(selectedAssetSelector);
  const selectedTool               = useSelector(selectedToolSelector);
  const disconnectedToolTagReport  = useSelector(disconnectedToolTagReportSelector);
  const searchSelected             = useSelector(searchSelectedSelector);
  const toolDetailView             = useSelector(toolDetailViewSelector);
  const selectedIonTag             = parentDevice?.isScDevice() && selectedTool;
  const dispatch                   = useDispatch();
  const media                      = useMedia();
  const {isCurrent}                = useThemeContext();
  const [,, resetToolsAndFindNearBy] = useSelectAssetAndResets();

  // current theme: the tool card's assignment link - back to the asset whose
  // details it was opened from, or on to the other asset it is assigned to ...
  const onOpenParent = useCallback(function() {
    resetToolsAndFindNearBy(false, Route.TRACKING2);
    dispatch(setAssetDetailView({value: true}) as any);
  }, [dispatch, resetToolsAndFindNearBy]);

  const onOpenAsset = useCallback(async function(assetId: string) {
    const assets = await getCVSAssets({assetId: Number(assetId)} as any, true);
    if (assets?.[0]) {
      dispatch(onAssetSelected({asset: assets[0], media, resetTool: true}) as any);
    }
  }, [dispatch, media]);

  const getToolCardListContent = useCallback((props) => (
    <ToolCardListContent {...props} />
  ), []);

  const onIssueClick = useCallback(function() {
    setToolInfoOpen(!toolInfoOpen);
  }, [toolInfoOpen]);

  if (selectedIonTag) {
    return (
      <IonTagDetailsPanel device={disconnectedToolTagReport} selectedIonTag={selectedIonTag}/>
    );
  }
  if (selectedTool) {
    const {
      tagReport : {
        deviceName
      } = {}
    } = selectedTool;
    if (isCurrent) {
      return (
        <ToolCard {...getToolCardProps({
          tool                  : selectedTool,
          parentAsset           : parentDevice,
          disconnectedTagReport : disconnectedToolTagReport?.tagReport,
          onOpenParent,
          onOpenAsset
        })} />
      );
    }
    return (
      <div className={classes.toolDetailWrapper}>
        <AssetCard asset={selectedTool}
          toolDetailView={toolDetailView}
          searchSelected={searchSelected}
          renderList={getToolCardListContent}
          toolPanelLabels={toolPanelLabels}
          toolConnected={!!selectedTool.tagReport}
          disconnectedToolTagReport={disconnectedToolTagReport} />
        <Divider />
        {
          deviceName && (
            <List>
              <AssetDetailListItem title={translate('tracking:toolDetail.tagDetail')}
                issueClick={onIssueClick}
                listOpen={toolInfoOpen}>
                <CardItemRow label='Name'
                  isLabelColon
                  value={deviceName} />
              </AssetDetailListItem>
            </List>
          )
        }
      </div>
    );
  }
  return null;
};

export default React.memo(TagTrackedAssetsPanel);
